import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, CheckCircle, XCircle, Warning, Info, Target } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useConsensusGroups from '../hooks/useConsensusGroups';
import { GROUP_STYLE, ConsensusRefLink, QualityBadge, computeTicketQuality } from '../components/ConsensusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pass: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-800' },
  fail: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-800' },
  warning: { icon: Warning, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-800' },
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-800' },
};

const VERDICT_CONFIG = {
  EXCELLENT: { color: 'from-emerald-500 to-green-600', text: 'Excellent Prediction!' },
  GOOD: { color: 'from-blue-500 to-cyan-600', text: 'Good Prediction' },
  FAIR: { color: 'from-amber-500 to-orange-600', text: 'Fair — Needs Adjustment' },
  POOR: { color: 'from-red-500 to-rose-600', text: 'Poor — Statistically Unlikely' },
};

export default function PredictionChecker() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [balls, setBalls] = useState(['', '', '', '', '']);
  const [stars, setStars] = useState(['', '']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const consensus = useConsensusGroups();

  // Auto-populate from URL params (e.g. ?b=1,2,3,4,5&ls=1,2)
  useEffect(() => {
    const bParam = searchParams.get('b');
    const lsParam = searchParams.get('ls');
    if (bParam) {
      const parsed = bParam.split(',').map(s => s.trim());
      if (parsed.length === 5) setBalls(parsed);
    }
    if (lsParam) {
      const parsed = lsParam.split(',').map(s => s.trim());
      if (parsed.length === 2) setStars(parsed);
    }
  }, [searchParams]);

  const updateBall = (idx, val) => {
    const next = [...balls];
    next[idx] = val;
    setBalls(next);
  };
  const updateStar = (idx, val) => {
    const next = [...stars];
    next[idx] = val;
    setStars(next);
  };

  const handleCheck = async () => {
    const parsedBalls = balls.map(Number);
    const parsedStars = stars.map(Number);
    if (parsedBalls.some(b => !b || b < 1 || b > 50)) { toast.error('All 5 balls must be between 1-50'); return; }
    if (parsedStars.some(s => !s || s < 1 || s > 12)) { toast.error('Both lucky stars must be between 1-12'); return; }
    if (new Set(parsedBalls).size < 5) { toast.error('All 5 balls must be unique'); return; }
    if (parsedStars[0] === parsedStars[1]) { toast.error('Lucky stars must be different'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/analysis/prediction-checker`, { balls: parsedBalls, lucky_stars: parsedStars });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check failed');
    } finally { setLoading(false); }
  };

  const handleSavePick = async () => {
    if (!result) return;
    try {
      await axios.post(`${API}/picks`, { balls: result.prediction.balls, lucky_stars: result.prediction.lucky_stars, notes: 'Saved from Prediction Checker' });
      toast.success('Saved to My Picks!');
    } catch (e) { toast.error('Failed to save pick'); }
  };

  const handleClear = () => { setBalls(['', '', '', '', '']); setStars(['', '']); setResult(null); };

  const verdict = result ? VERDICT_CONFIG[result.summary.verdict] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Target size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-lg font-bold text-gray-900">W48: Prediction Checker</h1><p className="text-xs text-gray-500">Validate your own numbers against historical parameters</p></div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Input Section */}
        <div className="bg-white border rounded-xl p-6 mb-6">
          <h2 className="font-bold text-gray-900 mb-4">Enter Your Prediction</h2>
          <div className="flex flex-col md:flex-row gap-6 items-end">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2 block">Main Balls (1-50)</label>
              <div className="flex gap-2">
                {balls.map((b, i) => (
                  <Input key={i} type="number" min="1" max="50" placeholder={`B${i + 1}`} value={b}
                    onChange={e => updateBall(i, e.target.value)}
                    className="w-20 text-center font-mono text-lg" data-testid={`input-ball-${i}`} />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2 block flex items-center gap-1">
                <Star size={12} weight="fill" className="text-yellow-500" /> Lucky Stars (1-12)
              </label>
              <div className="flex gap-2">
                {stars.map((s, i) => (
                  <Input key={i} type="number" min="1" max="12" placeholder={`LS${i + 1}`} value={s}
                    onChange={e => updateStar(i, e.target.value)}
                    className="w-20 text-center font-mono text-lg border-yellow-300 focus:border-yellow-500" data-testid={`input-star-${i}`} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCheck} disabled={loading} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white px-6" data-testid="check-btn">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : 'Check Prediction'}
              </Button>
              <Button variant="outline" onClick={handleClear} data-testid="clear-btn">Clear</Button>
            </div>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-6" data-testid="prediction-results">
            {/* Verdict Banner */}
            <div className={`bg-gradient-to-r ${verdict?.color} rounded-xl p-6 text-white shadow-lg`} data-testid="verdict-banner">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{verdict?.text}</h2>
                  <p className="text-white/80 text-sm mt-1">{result.summary.verdict_detail}</p>
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className="bg-white/20 px-3 py-1 rounded-full">{result.summary.passed} Passed</span>
                    {result.summary.warnings > 0 && <span className="bg-white/20 px-3 py-1 rounded-full">{result.summary.warnings} Warnings</span>}
                    {result.summary.failed > 0 && <span className="bg-white/20 px-3 py-1 rounded-full">{result.summary.failed} Failed</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {result.prediction.balls.map((b, i) => {
                    const g = consensus.groupOf(b);
                    const gs = GROUP_STYLE[g];
                    return (
                      <span key={i} className={`w-11 h-11 rounded-full bg-white/20 flex items-center justify-center font-mono font-bold text-lg ring-2 ring-offset-1 ring-offset-transparent ${gs.ring}`} title={`${g} · Consensus ${consensus.scoreOf(b) >= 0 ? '+' : ''}${consensus.scoreOf(b)}`}>{b}</span>
                    );
                  })}
                  <span className="text-white/40 text-xl mx-1">+</span>
                  {result.prediction.lucky_stars.map((ls, i) => (
                    <span key={`ls-${i}`} className="w-11 h-11 rounded-full bg-yellow-400/30 flex items-center justify-center font-mono font-bold text-lg text-yellow-200">{ls}</span>
                  ))}
                </div>
              </div>
              {/* Consensus Mix chip */}
              {(() => {
                const q = computeTicketQuality(result.prediction.balls, consensus);
                const mix = q.mix;
                return (
                  <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="pc-consensus-mix">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white/80">🚦 Quality:</span>
                    <QualityBadge tier={q.tier} score={q.score} size="md" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white/80 ml-2">Mix:</span>
                    {["GREEN","YELLOW","RED"].map(gk => (
                      <span key={gk} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${GROUP_STYLE[gk].chip}`}>
                        <span className={`inline-block w-2 h-2 rounded-full ${GROUP_STYLE[gk].dot}`} />
                        {gk}: {mix[gk]}
                      </span>
                    ))}
                    <ConsensusRefLink className="text-white hover:text-amber-300" />
                  </div>
                );
              })()}
              <div className="mt-4">
                <Button variant="secondary" size="sm" onClick={handleSavePick} className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="save-pick-btn">
                  Save to My Picks
                </Button>
              </div>
            </div>

            {/* Detailed Checks */}
            <div className="space-y-3">
              {['Structure', 'Pattern', 'Frequency', 'Lucky Stars', 'History'].map(cat => {
                const catChecks = result.checks.filter(c => c.category === cat);
                if (catChecks.length === 0) return null;
                return (
                  <div key={cat}>
                    <h3 className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-2">{cat}</h3>
                    <div className="space-y-2">
                      {catChecks.map((check, idx) => {
                        const cfg = STATUS_CONFIG[check.status];
                        const Icon = cfg.icon;
                        return (
                          <div key={idx} className={`border rounded-lg p-4 ${cfg.bg}`} data-testid={`check-${check.name.replace(/\s+/g, '-').toLowerCase()}`}>
                            <div className="flex items-start gap-3">
                              <Icon size={20} weight="fill" className={`${cfg.color} mt-0.5 shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-gray-900 text-sm">{check.name}</span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.badge}`}>{check.status}</span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-xs">
                                  <span className="font-mono text-gray-900">Value: <b>{typeof check.value === 'object' ? JSON.stringify(check.value) : check.value}</b></span>
                                  <span className="text-gray-500">Expected: {check.expected}</span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">{check.detail}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
